import {createTheme, Theme} from "@mui/material/styles";
import {GridBaseProps} from "@mui/material/Grid";
import "@mui/x-data-grid/themeAugmentation";
import {PaletteMode} from "@mui/material";

declare module "@mui/material/styles" {
  // allow configuration using `createTheme`
  interface Palette {
    paddingBottom: number;
    noData: string;
    scadaBackground: string;
    cardGrey: string;
    toolbarBackground: string;
    inverterProductionCurves: string[];
    immersionHeaterConsumptionCurves: string[];
    immersionHeaterTemperatureCurves: string[];
    trackerWindCurves: string[];
    trackerElevationCurves: string[];
    trackerExpectedElevationCurve: string;
    trackerAzimuthCurves: string[];
    trackerExpectedAzimuthCurve: string;
    eventChartLabel: string;
    plantProductionCurve: string;
    plantConsumptionCurve: string;
    plantWindCurve: string;
    essSoCCurves: string[];
    windThresoldCurve: string;
    snowCurve: string;
    trackerAlarmCurve: string;
    immersionHeaterAlarmCurve: string;
    soutirageEnedisCurve: string;
    notSynchronised: string;
    secondaryFont: string;
    neutralColor: string;
    checkpointResultFailed: string;
    checkpointResultInProgress: string;
    checkpointResultOk: string;
    checkpointResultUncertain: string;
    checkpointResultKo: string;
    toControl: string;
    compliant: string;
    notCompliant: string;
    controlInProgress: string;
    dialogCloseButtonBackground: string;
    link: string;
  }
  interface PaletteOptions {
    noData: string;
    scadaBackground: string;
    toolbarBackground: string;
    inverterProductionCurves: string[];
    immersionHeaterConsumptionCurves: string[];
    immersionHeaterTemperatureCurves: string[];
    trackerWindCurves: string[];
    trackerElevationCurves: string[];
    trackerExpectedElevationCurve: string;
    trackerAzimuthCurves: string[];
    trackerExpectedAzimuthCurve: string;
    essSoCCurves: string[];
    eventChartLabel: string;
    plantProductionCurve: string;
    plantConsumptionCurve: string;
    plantWindCurve: string;
    windThresoldCurve: string;
    snowCurve: string;
    trackerAlarmCurve: string;
    immersionHeaterAlarmCurve: string;
    soutirageEnedisCurve: string;
    notSynchronised: string;
    secondaryFont: string;
    neutralColor: string;
    link: string;
  }
}

const baseTheme = (mode: string) => {
  return {
    palette: {
      paddingBottom: 20,
      mode: mode as PaletteMode,
      primary: {
        main: "#35ba07",
        light: "#808eba",
      },
      secondary: {
        main: "#044589",
        light: "#7ecbb6",
      },
      success: {
        main: "#35ba07",
      },
      info: {
        main: "#35ba07",
      },
      warning: {
        main: "#F7B32B",
      },
      error: {
        main: "#D72638",
      },
      dialogCloseButtonBackground: "#35ba071f",
      // Les liens tombaient sur le bleu par défaut du navigateur, illisible sur fond
      // sombre. Le mode clair conserve ce bleu, seul le mode sombre est éclairci.
      link: mode === "dark" ? "#4dd0e1" : "#0000ee",
      noData: "#8e8e8e",
      toolbarBackground: "#eaeaea",
      scadaBackground: "#ffffff",
      cardGrey: "#F5F5F5",
      inverterProductionCurves: [
        "#A6A817",
        "#80A500",
        "#5D9A34",
        "#2E6F3D",
        "#83f8cd",
        "#3BBA8A",
        "#005A4A",
        "#9BCA4D",
        "#FFC300",
        "#D18501",
        "#B04B08",
        "#8FBC8F",
        "#4682B4",
        "#5F9EA0",
        "#6A5ACD",
        "#492EF5",
        "#B0E0E6",
        "#9A9A9A",
        "#C0C0C0",
        "#F5E5B8",
        "#E1C78D",
        "#FDDA8C",
      ],
      immersionHeaterConsumptionCurves: ["#ece7f2", "#d0d1e6", "#a6bddb", "#3690c0", "#74a9cf", "#0570b0", "#045a8d"],
      immersionHeaterTemperatureCurves: ["#e6550d", "#fd8d3c", "#a63603", "#fdae6b", "#d94801", "#f16913", "#8c2d04"],
      trackerWindCurves: [
        "#54424B",
        "#7A4E69",
        "#8A496F",
        "#995A82",
        "#A06C94",
        "#915F89",
        "#A06D9B",
        "#A477B0",
        "#9675A1",
        "#9B7FA9",
        "#A98FC4",
        "#9C8BB7",
        "#A8A2C6",
        "#A2A1C9",
        "#B6A5D1",
        "#B1B3E0",
        "#C6C8E9",
        "#C9D3F1",
        "#B8B7D3",
        "#ACB7DA",
        "#A2B4D6",
      ],
      trackerElevationCurves: [
        "#A6A817",
        "#80A500",
        "#5D9A34",
        "#2E6F3D",
        "#83f8cd",
        "#3BBA8A",
        "#005A4A",
        "#9BCA4D",
        "#FFC300",
        "#D18501",
        "#B04B08",
        "#8FBC8F",
        "#4682B4",
        "#5F9EA0",
        "#6A5ACD",
        "#492EF5",
        "#B0E0E6",
        "#9A9A9A",
        "#C0C0C0",
        "#F5E5B8",
        "#E1C78D",
        "#FDDA8C",
      ],
      trackerExpectedElevationCurve: "#89A6FB",
      trackerAzimuthCurves: [
        "#A6A817",
        "#80A500",
        "#5D9A34",
        "#2E6F3D",
        "#83f8cd",
        "#3BBA8A",
        "#005A4A",
        "#9BCA4D",
        "#FFC300",
        "#D18501",
        "#B04B08",
        "#8FBC8F",
        "#4682B4",
        "#5F9EA0",
        "#6A5ACD",
        "#492EF5",
        "#B0E0E6",
        "#9A9A9A",
        "#C0C0C0",
        "#F5E5B8",
        "#E1C78D",
        "#FDDA8C",
      ],
      essSoCCurves: ["#fd8d3c", "#FAC545", "#F5BD0F"],
      trackerExpectedAzimuthCurve: "#89A6FB",
      eventChartLabel: "#000000",
      plantProductionCurve: "#ffd500",
      plantConsumptionCurve: "#05468c",
      plantWindCurve: "#BCD2EE",
      windThresoldCurve: "#D72638",
      snowCurve: "#DBDFAC",
      trackerAlarmCurve: "#BFAB25",
      immersionHeaterAlarmCurve: "#BFAB25",
      soutirageEnedisCurve: "#009870",
      notSynchronised: "#236AB6",
      secondaryFont: "rgb(0, 0, 0, 0.4)",
      neutralColor: "#808080",
      checkpointResultFailed: "red",
      checkpointResultInProgress: "gray",
      checkpointResultOk: "green",
      checkpointResultUncertain: "#3778bd",
      checkpointResultKo: "orange",
      toControl: "grey",
      compliant: "green",
      notCompliant: "orange",
      controlInProgress: "#3778bd",
    },
    typography: {
      fontFamily: "Montserrat",
    },
  };
};

const overrides = {
  components: {
    MuiGrid: {
      styleOverrides: {
        // Grid v1 posait `width: 100%` sur les conteneurs, ce que Grid v2 ne fait plus.
        // Les vues de domaine (FleetManagement, Scada, Platform, Resource) enveloppent
        // les pages dans une Box `display: flex` : sans cette largeur, un conteneur
        // racine se réduit à son contenu au lieu d'occuper toute la largeur disponible.
        // On exclut les conteneurs qui sont aussi des items dimensionnés (`size`),
        // dont la largeur est déjà calculée par la grille.
        root: ({ownerState}: {ownerState: GridBaseProps}) => {
          const size = ownerState.size as Record<string, unknown> | undefined;
          const isSizedItem = Object.keys(size ?? {}).length > 0;
          return ownerState.container && !isSizedItem ? {width: "100%"} : {};
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        cell: {
          // DataGrid v6 posait `display: flex; align-items: center` sur la cellule ;
          // v9 s'appuie sur `line-height: calc(var(--height) - 1px)` à la place. Le
          // contenu personnalisé des `renderCell` (drapeau de langue, badge de date,
          // listes) n'était donc plus centré verticalement mais collé en haut.
          display: "flex",
          alignItems: "center",
          // Le centrage étant rétabli par le flex, ce `line-height` de la hauteur de
          // ligne devient inutile — et nuisible : il gonfle la boîte de ligne des
          // textes, si bien qu'un texte et une icône voisins dans la même cellule ne
          // s'alignaient plus (icône 1px plus basse que la date, colonne Date de la
          // documentation). On revient à la valeur héritée, comme en v6.
          lineHeight: "normal",
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        // MUI v9 a ramené ce minimum de 56px à `theme.spacing(4.5)` (36px), ce qui
        // décale de 20px vers la gauche le texte de toutes les listes à icône :
        // menu de navigation, carte installation du contrôle, liste des clients,
        // sélecteur de parcs. On restaure la valeur d'origine une fois pour toutes.
        root: {
          minWidth: 56,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: () =>
          getTheme("light").unstable_sx({
            backgroundColor: "#044589",
          }),
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: () =>
          getTheme("light").unstable_sx({
            backgroundColor: "#35ba07",
            color: "white",
            width: "100%",
            bottom: 0,
            position: "fixed",
            alignItems: "center",
            display: "flex",
          }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: "20px!important",
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: "None",
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          paddingTop: "4px!important",
          paddingBottom: "4px!important",
        },
      },
    },
  },
};

// `locales` : traductions intégrées de MUI (voir i18n/languages.ts), fusionnées dans le thème pour
// que tous les composants en héritent sans prop `localeText`.
const getTheme = (mode: string, locales: object[] = []): Theme => {
  const theme = createTheme({
    ...baseTheme(mode),
    ...overrides,
  });

  // Règle sur le sélecteur d'élément, donc de spécificité plus faible que n'importe quelle
  // classe MUI : elle rattrape les `<a>` bruts et ceux de react-router, sans rien imposer
  // aux `<Link>` de MUI, qui gardent leur prop `color` (`inherit` dans le pied de page).
  return createTheme(
    theme,
    {
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            a: {color: theme.palette.link},
          },
        },
      },
    },
    ...locales,
  );
};

export default getTheme;
